import type { ReactNode } from "react";
import {
  Calendar,
  Car,
  KeyRound,
  Mail,
  MapPin,
  Phone,
  Radio,
  Receipt,
  User,
  Users,
} from "lucide-react";
import { formatIsoDateShort } from "@/lib/bookingDates";
import { guestTypeLabel } from "@/lib/guestType";
import { PitchPlanBadge } from "@/components/bookings/PitchPlanBadge";
import { StayCalendarNotes } from "@/components/bookings/StayCalendarNotes";
import { BookingCompanionsPanel } from "@/components/bookings/BookingCompanionsPanel";
import { pitchAssignmentLabel } from "@/lib/pitchAssignment";
import { formatStayAmount } from "@/lib/money";
import { displayBookingNotes, formatGuestFieldValue } from "@/lib/inquiryDisplay";
import type { AccessKeyFormValues } from "@/components/bookings/AccessKeyFields";
import type { Booking, Guest, Pitch } from "@/types/camping";

function InfoItem({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
}) {
  if (!value || value === "—") return null;
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 shrink-0 text-label-tertiary">{icon}</span>
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-label-tertiary">{label}</p>
        <p className="mt-0.5 text-sm text-label">{value}</p>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">{title}</h3>
      <div className="space-y-3 rounded-xl border border-separator bg-fill-tertiary/30 p-4">
        {children}
      </div>
    </section>
  );
}

export interface GuestStaySummaryProps {
  booking: Booking;
  guest: Guest | null;
  pitch: Pitch | null;
  /** Zugangsdaten für die Anzeige (z. B. aus dem Check-in-Schritt). */
  accessKeys?: AccessKeyFormValues;
  /** checkin: Angaben zum Vorlesen. checkout: kurze Entscheidungsübersicht. */
  mode?: "checkin" | "checkout";
}

function GlanceStat({
  label,
  value,
  extra,
}: {
  label: string;
  value: string;
  extra?: ReactNode;
}) {
  return (
    <div className="bg-card px-4 py-3">
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-label-tertiary">{label}</dt>
      <dd className="mt-1 text-sm font-semibold text-label">{value}</dd>
      {extra ? <div className="mt-1.5">{extra}</div> : null}
    </div>
  );
}

function partyGlance(adults: number, children: number, dogs: number): string {
  const parts = [adults === 1 ? "1 Erwachsener" : `${adults} Erwachsene`];
  if (children === 1) parts.push("1 Kind");
  else if (children > 0) parts.push(`${children} Kinder`);
  if (dogs === 1) parts.push("1 Hund");
  else if (dogs > 0) parts.push(`${dogs} Hunde`);
  return parts.join(" · ");
}

export function GuestStaySummary({
  booking,
  guest,
  pitch,
  accessKeys,
  mode = "checkin",
}: GuestStaySummaryProps) {
  const guestDisplayName = guest
    ? `${guest.last_name}, ${guest.first_name}`
    : "Unbekannter Gast";

  const pitchLabel = pitch
    ? `${pitch.code}${pitch.label ? ` · ${pitch.label}` : ""}`
    : booking.pitch_id
      ? booking.pitch_id.slice(0, 8)
      : pitchAssignmentLabel(booking.pitch_assignment_mode);

  const partyParts: string[] = [];
  const adults = booking.adults ?? 2;
  const children = booking.children ?? 0;
  const dogs = booking.dogs ?? 0;
  partyParts.push(`${adults} Erw.`);
  if (children > 0) partyParts.push(`${children} Kind.`);
  if (dogs > 0) partyParts.push(`${dogs} Hund${dogs > 1 ? "e" : ""}`);

  const licensePlate = [booking.license_plate, booking.license_plate_country]
    .filter(Boolean)
    .join(" · ");

  const guestAddress = guest
    ? [guest.street, [guest.postal_code, guest.city].filter(Boolean).join(" "), guest.country_code]
        .filter(Boolean)
        .join(", ")
    : null;

  const transponder = accessKeys?.transponder_id.trim() || booking.transponder_id;
  const keyNumber = accessKeys?.key_number.trim() || booking.key_number;
  const bookingMessage = displayBookingNotes(booking.notes);
  const guestBirthdate = guest?.date_of_birth
    ? formatGuestFieldValue("date_of_birth", guest.date_of_birth)
    : null;
  const nightsLabel = booking.nights === 1 ? "1 Nacht" : `${booking.nights} Nächte`;

  if (mode === "checkout") {
    const contact = [guest?.phone, guest?.email].filter(Boolean).join(" · ");
    const extraStats = [
      licensePlate
        ? { label: "Fahrzeug", value: licensePlate }
        : booking.vehicle_type
          ? { label: "Fahrzeug", value: booking.vehicle_type.replace(/_/g, " ") }
          : null,
      transponder ? { label: "Transponder", value: transponder } : null,
      keyNumber ? { label: "Schlüssel", value: keyNumber } : null,
    ].filter((item): item is { label: string; value: string } => Boolean(item));

    return (
      <div className="space-y-4">
        <section className="overflow-hidden rounded-xl border border-separator">
          <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-4">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-label-tertiary">
                Gast
              </p>
              <h3 className="mt-1 text-base font-semibold tracking-tight text-label">
                {guestDisplayName}
              </h3>
              <p className="mt-0.5 text-xs text-label-tertiary">{booking.booking_number}</p>
              {contact ? <p className="mt-1 text-sm text-label-secondary">{contact}</p> : null}
              {guestAddress ? (
                <p className="mt-0.5 text-sm text-label-secondary">{guestAddress}</p>
              ) : null}
              {guestBirthdate ? (
                <p className="mt-0.5 text-sm text-label-secondary">Geboren {guestBirthdate}</p>
              ) : null}
            </div>
            {guest?.guest_type ? (
              <span className="rounded-full bg-fill-tertiary px-2.5 py-0.5 text-xs font-medium text-label-secondary">
                {guestTypeLabel(guest.guest_type)}
              </span>
            ) : null}
          </div>
          <dl className="grid grid-cols-2 gap-px border-t border-separator bg-separator sm:grid-cols-4">
            <GlanceStat
              label="Stellplatz"
              value={pitchLabel}
              extra={<PitchPlanBadge booking={booking} />}
            />
            <GlanceStat
              label="Zeitraum"
              value={`${formatIsoDateShort(booking.arrival_date)} → ${formatIsoDateShort(booking.departure_date)}`}
              extra={
                <>
                  <span className="text-xs text-label-secondary">{nightsLabel}</span>
                  <StayCalendarNotes
                    arrivalDate={booking.arrival_date}
                    departureDate={booking.departure_date}
                  />
                </>
              }
            />
            <GlanceStat label="Personen" value={partyGlance(adults, children, dogs)} />
            <GlanceStat
              label="Gesamtbetrag"
              value={formatStayAmount(booking.total_amount, booking.currency, booking.price_pending)}
            />
            {extraStats.map((item) => (
              <GlanceStat key={item.label} label={item.label} value={item.value} />
            ))}
          </dl>
        </section>

        {booking.companions?.length ? (
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
              Mitreisende
            </h3>
            <div className="rounded-xl border border-separator bg-fill-tertiary/30 p-4">
              <BookingCompanionsPanel
                companions={booking.companions}
                className="space-y-3"
                hideTitle
              />
            </div>
          </section>
        ) : null}

        {bookingMessage ? (
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
              Nachricht
            </h3>
            <p className="rounded-xl border border-separator bg-fill-tertiary/30 p-4 text-sm text-label whitespace-pre-wrap">
              {bookingMessage}
            </p>
          </section>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-label-secondary">
        Bitte die Angaben dem Gast vorlesen und bestätigen lassen.
      </p>
      <div className="grid gap-5 sm:grid-cols-2">
        <Section title="Gast">
          <InfoItem icon={<User className="h-4 w-4" />} label="Name" value={guestDisplayName} />
          <InfoItem
            icon={<User className="h-4 w-4" />}
            label="Gasttyp"
            value={guest ? guestTypeLabel(guest.guest_type) : null}
          />
          <InfoItem icon={<Phone className="h-4 w-4" />} label="Telefon" value={guest?.phone} />
          <InfoItem icon={<Mail className="h-4 w-4" />} label="E-Mail" value={guest?.email} />
          <InfoItem
            icon={<Calendar className="h-4 w-4" />}
            label="Geburtsdatum"
            value={guestBirthdate}
          />
          <InfoItem icon={<MapPin className="h-4 w-4" />} label="Adresse" value={guestAddress} />
        </Section>

        <Section title="Aufenthalt">
          <div className="flex gap-3">
            <span className="mt-0.5 shrink-0 text-label-tertiary">
              <MapPin className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wide text-label-tertiary">
                Stellplatz
              </p>
              <p className="mt-0.5 text-sm text-label">{pitchLabel}</p>
              <div className="mt-1">
                <PitchPlanBadge booking={booking} />
              </div>
            </div>
          </div>
          <InfoItem
            icon={<Calendar className="h-4 w-4" />}
            label="Zeitraum"
            value={`${formatIsoDateShort(booking.arrival_date)} → ${formatIsoDateShort(booking.departure_date)} (${booking.nights} N.)`}
          />
          <StayCalendarNotes
            arrivalDate={booking.arrival_date}
            departureDate={booking.departure_date}
          />
          <InfoItem
            icon={<Users className="h-4 w-4" />}
            label="Personen"
            value={partyParts.join(" · ")}
          />
          <InfoItem
            icon={<Car className="h-4 w-4" />}
            label="Fahrzeug"
            value={
              licensePlate ||
              (booking.vehicle_type ? booking.vehicle_type.replace(/_/g, " ") : null)
            }
          />
          <InfoItem icon={<Radio className="h-4 w-4" />} label="Transponder" value={transponder} />
          <InfoItem
            icon={<KeyRound className="h-4 w-4" />}
            label="Schlüsselnummer"
            value={keyNumber}
          />
          <InfoItem
            icon={<Receipt className="h-4 w-4" />}
            label="Gesamtbetrag"
            value={formatStayAmount(booking.total_amount, booking.currency, booking.price_pending)}
          />
        </Section>
      </div>

      {booking.companions?.length ? (
        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
            Mitreisende
          </h3>
          <div className="rounded-xl border border-separator bg-fill-tertiary/30 p-4">
            <BookingCompanionsPanel
              companions={booking.companions}
              className="space-y-3"
              hideTitle
            />
          </div>
        </section>
      ) : null}

      {bookingMessage ? (
        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
            Nachricht
          </h3>
          <p className="rounded-xl border border-separator bg-fill-tertiary/30 p-4 text-sm text-label whitespace-pre-wrap">
            {bookingMessage}
          </p>
        </section>
      ) : null}
    </div>
  );
}
